import { Save, Loader2, RotateCcw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky action bar that pins to the bottom of the viewport when a form is
 * dirty. Consistent across doctor-side editors: "Unsaved changes"
 * indicator, Discard button, and Save button (loading state supported).
 *
 * Props:
 *   isDirty  — bool; when false the bar is hidden
 *   onSave   — click handler
 *   onDiscard — optional; click handler for the Discard button
 *   saving   — bool; renders a spinner + disables buttons
 *   saveLabel — string; default "Save changes"
 *   testId   — optional; used as data-testid prefix
 */
export default function StickySaveBar({ isDirty, onSave, onDiscard, saving, saveLabel = "Save changes", testId = "sticky-save-bar" }) {
    if (!isDirty && !saving) return null;
    return (
        <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
            <div className="max-w-5xl mx-auto px-4 pb-4">
                <div className="pointer-events-auto rounded-full shadow-lg border border-[#123B5D]/10 bg-white/95 backdrop-blur-md flex items-center justify-between gap-3 pl-4 pr-2 py-2"
                    data-testid={testId}>
                    <div className="flex items-center gap-2 text-sm text-[#D97706]">
                        <AlertCircle size={16} />
                        <span className="font-semibold">Unsaved changes</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {onDiscard && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onDiscard}
                                disabled={saving}
                                data-testid={`${testId}-discard`}
                            >
                                <RotateCcw size={13} /> Discard
                            </Button>
                        )}
                        <Button size="sm" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                            <span className="ml-1">{saving ? "Saving…" : saveLabel}</span>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
